// ---------------------------------------------------------------------------
// Reveal ordering for screen 07.
//
// The server is the pacer. `round.revealOrder` is the queue it built when it
// judged the round, and `round.revealCursor` is how much of that queue it has
// released, so every client shows the same answer at the same moment instead of
// each racing its own timer. Nothing in here counts anything down.
//
// The local `revealOrder` below survives for one job the server cannot do for
// us: the declared list shows every seated player, including anyone the server's
// queue does not mention, and those stragglers still need a stable place to sit.
// The ordering rule is the same one the engine uses -- nothing submitted first,
// then ascending declared length -- so the two halves of the list agree.
//
// **Where the stragglers sit is the whole of that agreement.** A seat the queue
// does not mention is almost always a seat with no submission at all, which is a
// dash; the engine reveals dashes first; so they go at the FRONT of the display
// order rather than after the queue. See `unqueued`.
//
// It cannot call `revealOrder` from @countdown/shared: that takes the
// server-side RoundState, whose submissions carry `lockedAt` and the full
// answer. The client sees ClientRound with ClientSubmission, which has no
// lockedAt at all -- it is deliberately sanitized out -- so the tie-break has to
// be something both differ on stably.
// ---------------------------------------------------------------------------

import type { ClientRound, ClientSubmission, PublicPlayer } from '@countdown/shared';

/**
 * The closing beats now live in `@countdown/shared`, and this file only passes
 * them through.
 *
 * They were here because the "how long does a game take" estimate reads them
 * too, and a local constant kept both callers honest with each other. That is
 * no longer enough. A room can now set `revealPacePercent`, and every other
 * beat in the reveal — the lead, each entry's hold, the floor — is scaled by it
 * inside the engine. A payoff left behind as a frontend constant would be the
 * one part of the sequence the slider could not reach, so a room that slowed
 * the reveal down would still get the punchline at full speed: exactly the
 * complaint the longer beat was added to answer, reintroduced through the back
 * door.
 *
 * `REVEAL_PAYOFF_BEAT_MS` is 6s in shared, up from the 4s this file used to
 * declare. That difference is the "the board had" slide getting more time to be
 * read, which is a rule of the show rather than a client animation, so shared
 * is where it belongs.
 *
 * Call `revealPayoffMs(settings.revealPacePercent)` rather than these — the
 * re-exports exist for tests and for callers with no settings to hand.
 */
export { REVEAL_PAYOFF_BEAT_MS, REVEAL_PAYOFF_LEAD_MS, revealPayoffMs } from '@countdown/shared';

/** A submission with nothing in it. Shown as a dash, revealed first. */
function submittedNothing(submission: ClientSubmission | undefined): boolean {
  if (submission === undefined) return true;
  if (submission.declaredLength === null) return true;
  // Present only after the reveal, when the answer itself has arrived.
  if (submission.word !== undefined) return submission.word.trim().length === 0;
  if (submission.answer !== undefined) return submission.answer.trim().length === 0;
  if (submission.value !== undefined) return submission.value === null;
  return false;
}

/** Nothing-submitted first, then ascending declared length. */
export function revealOrder(round: ClientRound, players: PublicPlayer[]): string[] {
  const entries = players.map((player) => {
    const submission = round.submissions[player.id];
    return {
      playerId: player.id,
      nothing: submittedNothing(submission),
      declared: submission?.declaredLength ?? 0,
    };
  });

  entries.sort(
    (a, b) =>
      Number(b.nothing) - Number(a.nothing) ||
      a.declared - b.declared ||
      // Player id, because the client has no lock times to break a tie with. It
      // is arbitrary but stable, so the order does not shuffle between renders.
      (a.playerId < b.playerId ? -1 : a.playerId > b.playerId ? 1 : 0),
  );

  return entries.map((entry) => entry.playerId);
}

/**
 * The server's queue, restricted to players who are still seated.
 *
 * An empty queue is a real answer, not a missing one: a round nobody submitted
 * anything in has nothing to pace, and the engine sends `[]`. Treating that as
 * "no queue, show everything" is what put a released player with no submission
 * behind every answer frame, and a solo player who let one clock run out got a
 * crash for it.
 */
function serverQueue(round: ClientRound, seated: Set<string>): string[] {
  return (round.revealOrder ?? []).filter((playerId) => seated.has(playerId));
}

/**
 * The seats the server's queue does not mention, split by whether they have
 * anything to show.
 *
 * **A player who never touched the keyboard has no submission at all**, and
 * that is not the same thing as a player who locked an empty answer. The engine
 * builds `revealOrder` out of `round.submissions`, and `judgeCurrentRound`
 * adopts a draft only when there is something in it, so somebody who sat the
 * round out is in neither. They are seated, they are drawn as a dash, and the
 * server never paces them — there is nothing to pace.
 *
 * The engine's own rule is that an entry with nothing in it comes first (see
 * `revealOrder` in `@countdown/shared`), so a seat with no submission at all
 * belongs at the front for the same reason: the reveal builds toward the answers
 * that matter rather than trailing off into a run of dashes. It used to trail
 * off badly — everything here came out after the whole queue, which in a letters
 * round meant a handful of dashes landing on top of the leaderboard, above the
 * best word, in the frame the reveal completed.
 *
 * Anyone else the queue has failed to mention is a payload disagreeing with
 * itself rather than a player with nothing, so they stay behind the queue where
 * they were. **This is the only thing that moved**: where the empty entries sit.
 */
function unqueued(
  round: ClientRound,
  players: PublicPlayer[],
  queued: string[],
): { empty: string[]; answered: string[] } {
  const placed = new Set(queued);
  const rest = revealOrder(round, players).filter((playerId) => !placed.has(playerId));
  return {
    empty: rest.filter((playerId) => submittedNothing(round.submissions[playerId])),
    answered: rest.filter((playerId) => !submittedNothing(round.submissions[playerId])),
  };
}

/**
 * Every seated player in the order they come out: the seats with nothing in them
 * that the server never queued, then the server's queue, then anything left.
 *
 * The declared list renders all of these at once, because declared lengths go
 * public the moment the clock stops. Only the answers are paced.
 */
export function revealDisplayOrder(round: ClientRound, players: PublicPlayer[]): string[] {
  const seated = new Set(players.map((player) => player.id));
  const queued = serverQueue(round, seated);
  const { empty, answered } = unqueued(round, players, queued);
  return [...empty, ...queued, ...answered];
}

/**
 * Whose answer the server has actually let out. Everything the screen shows past
 * a declared length is gated on this, so a submission that arrives early in the
 * payload still cannot be rendered early.
 *
 * Two signals, both from the server, and no inference from anything else.
 * `revealComplete` opens the whole board, which is how a player who joined after
 * the reveal played out sees all of it at once; otherwise the cursor is the
 * limit, clamped, because a cursor that ran past the end of a shortened queue
 * must not release a player who is not in it.
 *
 * **The clamp is load-bearing and not defensive tidiness.** The reveal runs one
 * step past the end of its queue on purpose — that step releases nobody, it is
 * the last answer's own hold — so `revealCursor` reaches `revealOrder.length + 1`
 * in every ordinary round. Unclamped, the slice would be fine and the reasoning
 * behind it would not: the day something reads the cursor as an index, it is one
 * past the end.
 *
 * This used to read `best !== null` as "the reveal must be over". It is not the
 * same thing — the server can hold the best answer on the round before the
 * reveal has finished — and treating it as one released every seated player in a
 * round nobody had submitted anything in. The engine's own flag says what the
 * engine means, so ask it rather than guess from a neighbouring field.
 *
 * A payload old enough to carry neither field releases nothing, which shows less
 * than it could rather than more than it should. That is the right direction to
 * fail in, and it cannot ship anyway: one container serves the client and the
 * state that feeds it.
 */
export function releasedIds(round: ClientRound, players: PublicPlayer[]): string[] {
  const order = revealDisplayOrder(round, players);
  if (round.revealComplete === true) return order;

  // **The cursor counts the server's queue, not the display order.** Since the
  // unqueued dashes moved to the front, the two lengths differ, and slicing the
  // display order by the cursor directly would spend the server's releases on
  // players the server is not pacing — the best answer in the round would then
  // be the one left behind the end of the queue, which is the bug the trailing
  // step was added to fix, reintroduced from the other end.
  //
  // So they are released alongside the first paced entry rather than instead of
  // it: nothing while the lead beat runs and the stage is deliberately blank,
  // and then all of them at once, because the server has allotted them no time
  // and the client may not invent any.
  const seated = new Set(players.map((player) => player.id));
  const queued = serverQueue(round, seated);
  const lead = unqueued(round, players, queued).empty.length;

  const cursor = Math.min(Math.max(round.revealCursor ?? 0, 0), queued.length);
  return cursor === 0 ? [] : order.slice(0, lead + cursor);
}

/** Every answer is out and the best the board allowed may be shown. */
export function revealHasFinished(round: ClientRound): boolean {
  return round.revealComplete === true;
}
